import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from './components/Navbar';
import Footer from './components/Footer';

import fachada from './assets/images/6-768x1024.jpg';
import civil from './assets/images/1.jpg';
import iluminacao from './assets/images/2.png';
import transmissao from './assets/images/3.jpg';

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0 }
};

const stagger = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.12
    }
  }
};

const sectionReveal = {
  initial: 'hidden',
  whileInView: 'show',
  viewport: { once: true, amount: 0.18 },
  variants: fadeUp,
  transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] }
};

function SectionTag({ children, light = false }) {
  return (
    <span
      style={{
        display: 'inline-block',
        marginBottom: '14px',
        fontSize: '0.82rem',
        fontWeight: 700,
        letterSpacing: '1.4px',
        textTransform: 'uppercase',
        color: light ? '#d6a04a' : '#b7832f'
      }}
    >
      {children}
    </span>
  );
}

function SectionTitle({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <h2
      style={{
        fontSize: 'clamp(2rem, 3vw, 3.2rem)',
        lineHeight: 1.12,
        fontWeight: 800,
        letterSpacing: '-0.8px',
        color: light ? '#ffffff' : '#0d2e63',
        marginBottom: '18px',
        maxWidth,
        marginLeft: center ? 'auto' : 0,
        marginRight: center ? 'auto' : 0
      }}
    >
      {children}
    </h2>
  );
}

function SectionText({ children, center = false, light = false, maxWidth = '760px' }) {
  return (
    <p
      style={{
        fontSize: '1.04rem',
        lineHeight: 1.9,
        color: light ? 'rgba(255,255,255,0.78)' : '#5f7088',
        maxWidth,
        margin: center ? '0 auto' : 0
      }}
    >
      {children}
    </p>
  );
}

function About() {
  const [aboutData, setAboutData] = useState(null);

  useEffect(() => {
    async function loadAbout() {
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'https://real-backend-2026.onrender.com';
        const response = await fetch(`${apiUrl}/api/public/about`, {
          cache: 'no-store'
        });

        if (!response.ok) {
          throw new Error('Falha ao buscar conteúdo da página Nossa História');
        }

        const result = await response.json();
        setAboutData(result.data || null);
      } catch (error) {
        console.error('Erro ao carregar About:', error);
      }
    }

    loadAbout();
  }, []);

  const fallbackTimeline = useMemo(
    () => [
      {
        title: '1992',
        text: 'Início da trajetória da RealEnergy, com atuação voltada à representação de materiais elétricos.'
      },
      {
        title: 'Expansão técnica',
        text: 'Ao longo dos anos, a empresa ampliou sua presença no setor elétrico, participando de projetos de eletrificação rural, linhas de transmissão, subestações e infraestrutura urbana.'
      },
      {
        title: '2015',
        text: 'Especialização em gestão de parques de iluminação pública e privada, com forte know-how em luminárias LED, telemetria e eficiência energética.'
      },
      {
        title: 'Hoje',
        text: 'Atuação diversificada, equipe multidisciplinar e foco contínuo em inovação, qualidade, segurança e desenvolvimento de soluções.'
      }
    ],
    []
  );

  const fallbackValues = useMemo(
    () => [
      {
        title: 'Missão',
        text: 'Contribuir com o mercado da engenharia por meio do desenvolvimento de produtos e serviços alinhados aos melhores padrões de qualidade, desempenho técnico e confiabilidade operacional.'
      },
      {
        title: 'Visão',
        text: 'Ser referência no progresso do setor energético no Brasil e ampliar essa presença com inovação, eficiência, responsabilidade e respeito aos colaboradores, clientes, parceiros e à sociedade.'
      },
      {
        title: 'Valores',
        text: 'Atuar com compromisso econômico, social, cultural e sustentável, fortalecendo relações duradouras, confiança nas equipes e responsabilidade com cada projeto executado.'
      }
    ],
    []
  );

  const fallbackHighlights = useMemo(
    () => [
      {
        title: '30+ anos de experiência',
        text: 'Uma trajetória construída com consistência, evolução técnica e atuação em diferentes frentes do setor.'
      },
      {
        title: 'Equipe multidisciplinar',
        text: 'Profissionais capacitados em áreas como eletromecânica, eletroeletrônica, civil, light design, química e arquitetura.'
      },
      {
        title: 'Inovação aplicada',
        text: 'Busca contínua por tecnologias e modelos de atuação que ampliam eficiência, controle e qualidade nas entregas.'
      }
    ],
    []
  );

  const hero = aboutData?.hero || {};
  const story = aboutData?.story || {};
  const timeline = aboutData?.timeline || {};
  const structure = aboutData?.structure || {};
  const values = aboutData?.values || {};
  const finalBlock = aboutData?.finalBlock || {};

  const highlights = aboutData?.highlights?.length ? aboutData.highlights : fallbackHighlights;
  const timelineItems = timeline?.items?.length ? timeline.items : fallbackTimeline;
  const valueCards = values?.items?.length ? values.items : fallbackValues;

  const heroBackground = hero.backgroundImageUrl || fachada;
  const storyPrimaryImage = story.primaryImageUrl || civil;
  const storySecondaryImage = story.secondaryImageUrl || transmissao;
  const structureImage = structure.imageUrl || iluminacao;
  const finalBackground = finalBlock.backgroundImageUrl || fachada;

  return (
    <div
      style={{
        background: 'linear-gradient(180deg, #f3f6fb 0%, #ffffff 30%, #f7f9fc 100%)',
        color: '#11233e',
        fontFamily: 'Arial, sans-serif',
        overflow: 'hidden'
      }}
    >
      <Navbar />

      <section
        style={{
          position: 'relative',
          minHeight: '72vh',
          display: 'flex',
          alignItems: 'center',
          backgroundImage: `linear-gradient(95deg, rgba(8,20,41,0.95) 0%, rgba(10,33,70,0.9) 42%, rgba(13,52,110,0.68) 100%), url(${heroBackground})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(circle at 15% 20%, rgba(255,255,255,0.08), transparent 22%), radial-gradient(circle at 85% 12%, rgba(214,160,74,0.18), transparent 16%)'
          }}
        />

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          <div className="row align-items-center g-5">
            <div className="col-lg-7">
              <motion.div {...sectionReveal} style={{ maxWidth: '760px', padding: '50px 0' }}>
                <div
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px 16px',
                    borderRadius: '999px',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    color: 'rgba(255,255,255,0.84)',
                    fontSize: '13px',
                    textTransform: 'uppercase',
                    letterSpacing: '1px',
                    marginBottom: '24px'
                  }}
                >
                  {hero.badge || 'Sobre a RealEnergy'}
                </div>

                <h1
                  style={{
                    fontSize: 'clamp(2.6rem, 5vw, 4.7rem)',
                    lineHeight: 1.03,
                    fontWeight: 800,
                    letterSpacing: '-1.2px',
                    color: '#ffffff',
                    marginBottom: '22px'
                  }}
                >
                  {hero.title || 'Uma trajetória construída com experiência, inovação e compromisso com a excelência.'}
                </h1>

                <p
                  style={{
                    fontSize: '1.08rem',
                    lineHeight: 1.9,
                    color: 'rgba(255,255,255,0.82)',
                    maxWidth: '650px'
                  }}
                >
                  {hero.subtitle || 'Conheça a história da RealEnergy, sua evolução ao longo das décadas e os pilares que sustentam uma atuação sólida em energia, infraestrutura e serviços especializados.'}
                </p>
              </motion.div>
            </div>

            <div className="col-lg-5">
              <motion.div
                initial={{ opacity: 0, y: 34 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.08 }}
                style={{ position: 'relative', maxWidth: '500px', marginLeft: 'auto' }}
              >
                <motion.div
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
                  style={{
                    position: 'relative',
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,255,255,0.12)',
                    borderRadius: '30px',
                    padding: '18px',
                    backdropFilter: 'blur(16px)',
                    boxShadow: '0 30px 80px rgba(0,0,0,0.24)'
                  }}
                >
                  <img
                    src={heroBackground}
                    alt="Fachada RealEnergy"
                    style={{
                      width: '100%',
                      height: '420px',
                      objectFit: 'cover',
                      borderRadius: '24px',
                      display: 'block'
                    }}
                  />

                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.25 }}
                    whileHover={{ y: -4 }}
                    style={{
                      position: 'absolute',
                      left: '-20px',
                      bottom: '34px',
                      width: '240px',
                      background: 'rgba(255,255,255,0.97)',
                      borderRadius: '22px',
                      padding: '18px',
                      boxShadow: '0 20px 55px rgba(15, 23, 42, 0.18)'
                    }}
                  >
                    <div
                      style={{
                        fontSize: '0.78rem',
                        color: '#b7832f',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        marginBottom: '8px',
                        fontWeight: 700
                      }}
                    >
                      {hero?.card?.badge || 'Desde 1992'}
                    </div>
                    <div
                      style={{
                        fontWeight: 800,
                        fontSize: '1.12rem',
                        marginBottom: '6px',
                        color: '#0d2e63',
                        lineHeight: 1.3
                      }}
                    >
                      {hero?.card?.title || 'Crescimento com consistência e visão de futuro.'}
                    </div>
                    <div style={{ color: '#607089', fontSize: '0.95rem', lineHeight: 1.6 }}>
                      {hero?.card?.text || 'Uma empresa que evoluiu com o mercado e ampliou sua atuação com base em experiência e inovação.'}
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ marginTop: '-48px', position: 'relative', zIndex: 3 }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'rgba(255,255,255,0.95)',
              borderRadius: '28px',
              boxShadow: '0 24px 70px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)',
              padding: '30px'
            }}
          >
            <motion.div
              className="row g-4"
              variants={stagger}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.15 }}
            >
              {highlights.map((item, index) => (
                <div className="col-lg-4" key={item.id || index}>
                  <motion.div
                    variants={fadeUp}
                    transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                    whileHover={{ y: -8, boxShadow: '0 24px 48px rgba(13, 46, 99, 0.08)' }}
                    style={{
                      background: '#ffffff',
                      borderRadius: '22px',
                      padding: '28px 24px',
                      height: '100%',
                      border: '1px solid rgba(13, 46, 99, 0.06)'
                    }}
                  >
                    <div
                      style={{
                        width: '54px',
                        height: '54px',
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #edf3ff, #f7efe1)',
                        marginBottom: '18px'
                      }}
                    />
                    <h3
                      style={{
                        fontSize: '1.22rem',
                        fontWeight: 800,
                        marginBottom: '12px',
                        color: '#0d2e63'
                      }}
                    >
                      {item.title}
                    </h3>
                    <p style={{ color: '#607089', lineHeight: 1.8, marginBottom: 0 }}>{item.text}</p>
                  </motion.div>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '110px 0 70px' }}>
        <div className="container">
          <div className="row align-items-center g-5">
            <div className="col-lg-6">
              <motion.div {...sectionReveal}>
                <SectionTag>{story.tag || 'Nossa história'}</SectionTag>
                <SectionTitle>
                  {story.title || 'Evolução contínua em diferentes frentes do setor elétrico e da infraestrutura.'}
                </SectionTitle>
                <SectionText maxWidth="620px">{story.textOne || 'A RealEnergy é uma empresa privada criada em 1992, cuja atividade inicial esteve voltada à representação de materiais elétricos.'}</SectionText>
                <div style={{ height: '10px' }} />
                <SectionText maxWidth="620px">{story.textTwo || 'Ao longo de mais de três décadas, ampliou sua atuação no setor elétrico, participando de programas de eletrificação rural, projetos em linhas de transmissão e subestações de energia de até 500 kV, além de obras de infraestrutura urbana.'}</SectionText>
                <div style={{ height: '10px' }} />
                <SectionText maxWidth="620px">{story.textThree || 'Em 2015, a empresa passou a atuar de forma especializada na gestão de parques de iluminação pública e privada, consolidando know-how em luminárias LED, telemetria e soluções voltadas à eficiência energética.'}</SectionText>
                <div style={{ height: '10px' }} />
                <SectionText maxWidth="620px">{story.textFour || 'Com plataforma computacional própria, a REAL LUME, a RealEnergy desenvolveu uma gestão completa, inteligente e eficiente das unidades consumidoras de iluminação, possibilitando expressiva redução no consumo de energia.'}</SectionText>
              </motion.div>
            </div>

            <div className="col-lg-6">
              <motion.div {...sectionReveal} style={{ position: 'relative', minHeight: '560px' }}>
                <motion.img
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ duration: 0.35 }}
                  src={storyPrimaryImage}
                  alt="Atuação em infraestrutura"
                  style={{
                    width: '78%',
                    height: '420px',
                    objectFit: 'cover',
                    borderRadius: '30px',
                    boxShadow: '0 22px 60px rgba(15, 23, 42, 0.14)',
                    display: 'block'
                  }}
                />
                <motion.img
                  whileHover={{ y: -6, scale: 1.01 }}
                  transition={{ duration: 0.35 }}
                  src={storySecondaryImage}
                  alt="Linhas de transmissão"
                  style={{
                    width: '58%',
                    height: '260px',
                    objectFit: 'cover',
                    borderRadius: '26px',
                    boxShadow: '0 22px 60px rgba(15, 23, 42, 0.14)',
                    position: 'absolute',
                    right: 0,
                    bottom: 0,
                    border: '8px solid #f7f9fc'
                  }}
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <section style={{ padding: '20px 0 100px' }}>
        <div className="container">
          <motion.div {...sectionReveal} className="text-center" style={{ maxWidth: '820px', margin: '0 auto 54px' }}>
            <SectionTag>{timeline.tag || 'Linha do tempo'}</SectionTag>
            <SectionTitle center>
              {timeline.title || 'Marcos que representam a consolidação da RealEnergy ao longo do tempo.'}
            </SectionTitle>
            <SectionText center>
              {timeline.text || 'Uma trajetória marcada por expansão técnica, desenvolvimento de novas soluções e fortalecimento da atuação institucional.'}
            </SectionText>
          </motion.div>

          <motion.div
            className="row g-4"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {timelineItems.map((item, index) => (
              <div className="col-md-6 col-xl-3" key={item.id || index}>
                <motion.div
                  variants={fadeUp}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '24px',
                    padding: '26px 22px',
                    height: '100%',
                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                    border: '1px solid rgba(13, 46, 99, 0.05)'
                  }}
                >
                  <div
                    style={{
                      fontSize: '1.4rem',
                      fontWeight: 800,
                      color: '#0d2e63',
                      marginBottom: '12px'
                    }}
                  >
                    {item.title}
                  </div>
                  <p style={{ color: '#607089', lineHeight: 1.8, marginBottom: 0 }}>{item.text}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              background: 'linear-gradient(135deg, #ffffff, #f8fbff)',
              borderRadius: '30px',
              padding: '38px',
              boxShadow: '0 24px 60px rgba(15, 23, 42, 0.08)',
              border: '1px solid rgba(15, 23, 42, 0.05)'
            }}
          >
            <div className="row align-items-center g-4">
              <div className="col-lg-6">
                <SectionTag>{structure.tag || 'Estrutura técnica'}</SectionTag>
                <SectionTitle maxWidth="640px">
                  {structure.title || 'Uma equipe formada por conhecimento técnico e visão multidisciplinar.'}
                </SectionTitle>
                <SectionText maxWidth="640px">
                  {structure.textOne || 'Atualmente, a RealEnergy conta com profissionais altamente capacitados em diferentes áreas da engenharia, entre elas eletromecânica, eletroeletrônica, civil, light design, química e arquitetura.'}
                </SectionText>
                <div style={{ height: '10px' }} />
                <SectionText maxWidth="640px">
                  {structure.textTwo || 'Além disso, reúne centenas de outros profissionais que contribuem diretamente para o desenvolvimento das atividades e para a qualidade das entregas.'}
                </SectionText>
              </div>

              <div className="col-lg-6">
                <motion.img
                  whileHover={{ scale: 1.015 }}
                  transition={{ duration: 0.35 }}
                  src={structureImage}
                  alt="Iluminação e tecnologia"
                  style={{
                    width: '100%',
                    height: '360px',
                    objectFit: 'cover',
                    borderRadius: '26px',
                    boxShadow: '0 22px 60px rgba(15, 23, 42, 0.12)'
                  }}
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 100px' }}>
        <div className="container">
          <motion.div {...sectionReveal} className="text-center" style={{ maxWidth: '820px', margin: '0 auto 54px' }}>
            <SectionTag>{values.tag || 'Missão, visão e valores'}</SectionTag>
            <SectionTitle center>
              {values.title || 'Princípios que orientam a atuação e o crescimento da RealEnergy.'}
            </SectionTitle>
            <SectionText center>
              {values.text || 'Uma base institucional voltada à qualidade, ao respeito nas relações e ao desenvolvimento sustentável dos negócios.'}
            </SectionText>
          </motion.div>

          <motion.div
            className="row g-4"
            variants={stagger}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.1 }}
          >
            {valueCards.map((item, index) => (
              <div className="col-lg-4" key={item.id || index}>
                <motion.div
                  variants={fadeUp}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  whileHover={{ y: -10 }}
                  style={{
                    background: '#ffffff',
                    borderRadius: '26px',
                    padding: '30px 26px',
                    height: '100%',
                    boxShadow: '0 18px 48px rgba(15, 23, 42, 0.07)',
                    border: '1px solid rgba(13, 46, 99, 0.05)'
                  }}
                >
                  <div
                    style={{
                      width: '58px',
                      height: '58px',
                      borderRadius: '18px',
                      background: 'linear-gradient(135deg, #edf3ff, #f7efe1)',
                      marginBottom: '18px'
                    }}
                  />
                  <h3
                    style={{
                      fontSize: '1.3rem',
                      fontWeight: 800,
                      color: '#0d2e63',
                      marginBottom: '14px'
                    }}
                  >
                    {item.title}
                  </h3>
                  <p style={{ color: '#607089', lineHeight: 1.85, marginBottom: 0 }}>{item.text}</p>
                </motion.div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section style={{ padding: '0 0 110px' }}>
        <div className="container">
          <motion.div
            {...sectionReveal}
            style={{
              position: 'relative',
              overflow: 'hidden',
              borderRadius: '34px',
              padding: '64px 30px',
              backgroundImage: `linear-gradient(135deg, rgba(9,28,59,0.94), rgba(14,63,148,0.9)), url(${finalBackground})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              textAlign: 'center',
              boxShadow: '0 28px 72px rgba(15, 23, 42, 0.16)'
            }}
          >
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'radial-gradient(circle at top center, rgba(255,255,255,0.1), transparent 28%)'
              }}
            />

            <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 2 }}>
              <SectionTag light>{finalBlock.tag || 'Compromisso com o futuro'}</SectionTag>
              <SectionTitle light center maxWidth="800px">
                {finalBlock.title || 'Crescimento sustentado por inovação, qualidade técnica e foco em relacionamento duradouro.'}
              </SectionTitle>
              <SectionText light center maxWidth="760px">
                {finalBlock.text || 'O desenvolvimento da RealEnergy ao longo dos anos reflete a busca constante por novas tecnologias, modelos de negócio e soluções capazes de gerar valor para clientes, parceiros e para a sociedade.'}
              </SectionText>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default About;